/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useHistory, useLocation } from "react-router-dom";
import { useEffect } from "react";

export const canonicalize = (currentPath: string): string => {
  // Step 1: Convert the path to lowercase
  let canonicalPath = currentPath.toLowerCase();
  if (process.env.QA_LANGUAGE_ENABLED === "true") {
    // We may need a better solution for this if we have more region specific locales
    canonicalPath = canonicalPath
      .replace("/en-za/", "/en-ZA/")
      .replace(/\/en-za$/, "/en-ZA");
  }

  // Step 2: Remove trailing slash
  if (canonicalPath.endsWith("/")) {
    canonicalPath = canonicalPath.slice(0, -1);
  }

  return canonicalPath;
};

export const useCanonicalize = (): void => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    // Get the current URL path
    const currentPath = location.pathname;
    const canonicalPath = canonicalize(currentPath);

    // If the current path isn't in the canonical form, redirect
    if (currentPath !== canonicalPath) {
      // Create the new location object with the canonical path
      // and preserve query params and hash
      const newLocation = {
        pathname: canonicalPath,
        search: location.search,
        hash: location.hash,
      };

      // Replace the current URL with the canonical one
      history.replace(newLocation);
    }
  }, [location, history]);
};
